import type { IDBPDatabase } from "idb";
import type { AppDBSchema } from "~/types/database";

export default class UploadDbModule {
  private getDb: () => Promise<IDBPDatabase<AppDBSchema>>;

  constructor(getDb: () => Promise<IDBPDatabase<AppDBSchema>>) {
    this.getDb = getDb;
  }

  async saveSession(session: AppDBSchema["sessions"]["value"]) {
    const db = await this.getDb();
    await db.put("sessions", session);
  }

  async updateSessionFinished(
    uploadId: string,
    totalParts: number,
    duration: number,
  ) {
    const db = await this.getDb();
    const session = await db.get("sessions", uploadId);
    if (session) {
      session.isRecordingFinished = true;
      session.totalParts = totalParts;
      session.duration = duration;
      await db.put("sessions", session);
    }
  }

  async saveChunk(chunk: AppDBSchema["chunks"]["value"]) {
    const db = await this.getDb();
    await db.put("chunks", chunk);
  }

  async markChunkUploaded(id: string, tag: string) {
    const db = await this.getDb();
    const chunk = await db.get("chunks", id);
    if (chunk) {
      chunk.uploaded = true;
      chunk.tag = tag;
      await db.put("chunks", chunk);
    }
  }

  async getPendingChunks(uploadId: string) {
    const db = await this.getDb();
    const allChunks = await db.getAllFromIndex(
      "chunks",
      "by-uploadId",
      uploadId,
    );
    return allChunks.filter((c) => !c.uploaded);
  }

  async getAllSessions() {
    const db = await this.getDb();
    return await db.getAll("sessions");
  }

  async getAllChunks(uploadId: string) {
    const db = await this.getDb();
    return await db.getAllFromIndex("chunks", "by-uploadId", uploadId);
  }

  async clearSessionData(uploadId: string) {
    const db = await this.getDb();
    const tx = db.transaction(["sessions", "chunks"], "readwrite");

    await tx.objectStore("sessions").delete(uploadId);

    const chunkStore = tx.objectStore("chunks");
    const index = chunkStore.index("by-uploadId");
    let cursor = await index.openCursor(uploadId);
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
  }

  async getSession(uploadId: string) {
    const db = await this.getDb();
    return await db.get("sessions", uploadId);
  }

  async setRealSessionData(
    uploadId: string,
    realUploadId: string,
    realKey: string,
  ) {
    const db = await this.getDb();
    const session = await db.get("sessions", uploadId);
    if (session) {
      session.realUploadId = realUploadId;
      session.realKey = realKey;
      session.isStartedOnServer = true;
      await db.put("sessions", session);
    }
  }

  async markChunkForRetry(
    uploadId: string,
    partNumber: number,
    retryUrl: string,
  ) {
    const db = await this.getDb();
    const chunkId = `${uploadId}_${partNumber}`;
    const chunk = await db.get("chunks", chunkId);

    if (chunk) {
      chunk.uploaded = false;
      delete chunk.tag;
      chunk.retryUrl = retryUrl;
      await db.put("chunks", chunk);
    }
  }
}
